import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Card, Checkbox, Col, Empty, message, Popconfirm, Progress, Row, Skeleton, Space,
    Statistic, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd';
import { InfoCircleOutlined, LoadingOutlined, ReadOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi, ModelBeliefsGate, ModelBeliefsPageRow, ModelBeliefsPagesResponse,
    ModelBeliefsResultRow, ModelBeliefsRunDetail as RunDetailData,
    ModelBeliefsSources, ModelBeliefsSynthesis,
} from '../../../services/ai-admin-api';

const { Text, Title, Paragraph } = Typography;

const pct = (rate: number | null | undefined): string =>
    rate === null || rate === undefined ? '-' : `${Math.round(rate * 100)}%`;

// Scores run -100..+100 where 0 is NEUTRAL, not 50. Colouring them on a 0-100
// ramp would paint a genuinely neutral belief as a failing grade, which is the
// single most common way this scale gets misread.
const scoreColour = (score: number | null): string => {
    if (score === null) return '#8c8c8c';
    if (score >= 50) return '#237804';
    if (score >= 20) return '#52c41a';
    if (score >= 0) return '#faad14';
    if (score >= -40) return '#fa8c16';
    return '#cf1322';
};

export const ModelBeliefsRunDetail: React.FC<{ runId: number }> = ({ runId }) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [gate, setGate] = useState<ModelBeliefsGate | null>(null);
    const [sources, setSources] = useState<ModelBeliefsSources | null>(null);
    const [results, setResults] = useState<ModelBeliefsResultRow[]>([]);
    const [synthesis, setSynthesis] = useState<ModelBeliefsSynthesis | null>(null);
    const [pages, setPages] = useState<ModelBeliefsPagesResponse | null>(null);
    const [detail, setDetail] = useState<RunDetailData | null>(null);
    const [loading, setLoading] = useState(true);
    const [resummarising, setResummarising] = useState(false);
    const [startingRead, setStartingRead] = useState(false);
    const [retryUnreadable, setRetryUnreadable] = useState(true);

    const load = useCallback(async () => {
        if (!jwt) return;
        setLoading(true);
        const [g, s, r, syn, pg, d] = await Promise.all([
            aiAdminApi.getModelBeliefsGate(jwt, runId).catch(() => null),
            aiAdminApi.getModelBeliefsSources(jwt, runId).catch(() => null),
            aiAdminApi.getModelBeliefsResults(jwt, runId).catch(() => []),
            aiAdminApi.getModelBeliefsSynthesis(jwt, runId).catch(() => null),
            aiAdminApi.getModelBeliefsPages(jwt, runId).catch(() => null),
            aiAdminApi.getModelBeliefsRun(jwt, runId).catch(() => null),
        ]);
        setGate(g); setSources(s); setResults(r ?? []);
        setSynthesis(syn?.synthesis ?? null); setPages(pg); setDetail(d); setLoading(false);
    }, [jwt, runId]);

    useEffect(() => { void load(); }, [load]);

    // While a page read is working, the pages are re-fetched on their own
    // every ten seconds (the full load would flash the skeleton), and the
    // whole run is reloaded once when it finishes, so the synthesis tab and
    // the pages badge catch up without a manual refresh.
    useEffect(() => {
        if (!jwt || pages?.reading !== true) return undefined;
        const timer = window.setTimeout(async () => {
            const next = await aiAdminApi.getModelBeliefsPages(jwt, runId).catch(() => null);
            if (!next) return;
            if (next.reading) setPages(next);
            else await load();
        }, 10000);
        return () => window.clearTimeout(timer);
    }, [jwt, runId, pages, load]);

    const resummarise = async () => {
        setResummarising(true);
        try {
            await aiAdminApi.resummariseModelBeliefsGate(jwt, runId);
            message.success('Gate summary rebuilt from the stored answers.');
            await load();
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setResummarising(false);
        }
    };

    const readPagesAgain = async () => {
        setStartingRead(true);
        try {
            const r = await aiAdminApi.readModelBeliefsPages(jwt, runId, retryUnreadable);
            if (r.alreadyRunning) message.info('A page read is already running for this run.');
            else message.success(
                `Page read started${r.retried > 0 ? `, ${r.retried} unreadable page(s) put back to be fetched` : ''}. It re-writes the read when it finishes.`,
            );
            await load();
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setStartingRead(false);
        }
    };

    if (loading) return <Skeleton active paragraph={{ rows: 10 }} />;

    const operative = (gate?.summary?.byMode ?? []).find((m) => m.retrievalMode === 'on');
    const consensus = results.find((r) => r.provider === 'panel' && r.view === 'customer');
    const perModel = results.filter((r) => r.provider !== 'panel' && r.view === 'customer');

    const gateTab = !gate?.summary ? (
        <Empty description="No gate measured on this run" />
    ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space>
                <Popconfirm
                    title="Re-summarise the gate?"
                    description="Rebuilds the summary from this run's stored answers, so every brand the models named gets its rank score. Free: no model is called. Re-synthesise afterwards so the written read sees it."
                    okText="Re-summarise"
                    onConfirm={() => void resummarise()}
                >
                    <Button size="small" icon={<ReloadOutlined style={{ marginRight: 4 }} />} loading={resummarising}>
                        Re-summarise gate
                    </Button>
                </Popconfirm>
            </Space>
            <Row gutter={16}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Recommended"
                            value={pct(operative?.inclusionRate)}
                            suffix={operative ? `of ${operative.asked}` : ''}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={
                                <Space size={4}>
                                    Average position
                                    <Tooltip title="Averaged over the answers that named the brand only. A brand listed once at rank 1 is not better placed than one listed every time at rank 2 — that difference is the recommendation rate.">
                                        <InfoCircleOutlined />
                                    </Tooltip>
                                </Space>
                            }
                            value={operative?.meanPosition ?? '-'}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Models naming the brand"
                            value={`${operative?.modelsIncluding ?? 0} / ${operative?.modelsAsked ?? 0}`}
                        />
                    </Card>
                </Col>
            </Row>

            <Card size="small" title="Competitors in the same answers">
                <Table
                    size="small" pagination={false} rowKey="name"
                    dataSource={gate.summary.competitors}
                    columns={[
                        { title: 'Brand', dataIndex: 'name' },
                        { title: 'Recommended', dataIndex: 'inclusionRate', render: (v: number) => pct(v) },
                        { title: 'Avg position', dataIndex: 'meanPosition', render: (v: number | null) => v ?? '-' },
                        {
                            title: 'Why the models recommend them (verbatim)',
                            dataIndex: 'reasons',
                            render: (reasons: string[]) => (
                                <Space direction="vertical" size={2}>
                                    {reasons.map((r) => (
                                        <Text key={r} italic style={{ fontSize: 12 }}>“{r}”</Text>
                                    ))}
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>

            {gate.summary.unknownBrands.length > 0 && (
                <Card
                    size="small"
                    title="Other brands the models named"
                    extra={<Text type="secondary" style={{ fontSize: 12 }}>Not configured as competitors</Text>}
                >
                    <Space wrap>
                        {gate.summary.unknownBrands.map((b) => (
                            <Tag key={b.name}>{b.name} · {b.count}</Tag>
                        ))}
                    </Space>
                </Card>
            )}
        </Space>
    );

    const beliefsTab = !consensus ? (
        <Empty description="No scores yet" />
    ) : (
        <Card size="small" title={`Brand Equity: ${consensus.brandEquity ?? 'no belief held'}`}>
            <Table
                size="small" pagination={false} rowKey="element"
                dataSource={consensus.elements}
                columns={[
                    { title: 'Element', dataIndex: 'element' },
                    {
                        title: 'Panel consensus', dataIndex: 'score',
                        render: (score: number | null) =>
                            score === null ? (
                                <Tooltip title="The models hold no belief here. That is a finding, not missing data: nothing in the public record gave them a view either way.">
                                    <Text type="secondary">no belief held</Text>
                                </Tooltip>
                            ) : (
                                <Text strong style={{ color: scoreColour(score) }}>{score}</Text>
                            ),
                    },
                    ...perModel
                        .filter((m) => m.target === consensus.target)
                        .map((m) => ({
                            title: m.model ?? m.provider,
                            key: `${m.provider}-${m.model}`,
                            render: (_: unknown, row: { element: string }) => {
                                const match = m.elements.find((e) => e.element === row.element);
                                return match?.score === null || match?.score === undefined
                                    ? <Text type="secondary">-</Text>
                                    : <Text style={{ color: scoreColour(match.score) }}>{match.score}</Text>;
                            },
                        })),
                ]}
            />
        </Card>
    );

    const sourcesTab = !sources?.ledger ? (
        <Empty description="No sources recorded" />
    ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title="What the record is made of">
                {sources.ledger.byKind.map((k) => (
                    <div key={k.kind}>
                        <Text>{k.kind}</Text>
                        <Progress percent={Math.round(k.share * 100)} size="small" format={() => `${k.count}`} />
                    </div>
                ))}
            </Card>

            <Card size="small" title="Most-read domains">
                <Table
                    size="small" pagination={false} rowKey="domain"
                    dataSource={sources.ledger.entries.slice(0, 15)}
                    columns={[
                        { title: 'Domain', dataIndex: 'domain' },
                        { title: 'Kind', dataIndex: 'kind', render: (k: string) => <Tag>{k}</Tag> },
                        { title: 'Citations', dataIndex: 'count' },
                    ]}
                />
            </Card>

            {(sources.impostorCandidates?.length ?? 0) > 0 && (
                <Alert
                    type="warning" showIcon icon={<WarningOutlined />}
                    message="Sites that may be passing themselves off as the brand"
                    description={
                        <Space direction="vertical" size={4}>
                            <Text>
                                Candidates for the brand to check, never a confirmed
                                finding. A stockist, a review site and a news article
                                all carry the brand name too.
                            </Text>
                            {sources.impostorCandidates?.map((i) => (
                                <Text key={i.domain}>
                                    <Text strong>{i.domain}</Text> — read {i.count} time(s): {i.evidence.join('; ')}
                                </Text>
                            ))}
                        </Space>
                    }
                />
            )}
        </Space>
    );

    const synthesisTab = !synthesis ? (
        <Empty description="Not synthesised yet" />
    ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small">
                <Title level={5}>{synthesis.headline}</Title>
                <Paragraph>{synthesis.summary}</Paragraph>
                <Paragraph type="secondary">{synthesis.gate_note}</Paragraph>
            </Card>
            {synthesis.pages_note && (
                <Card size="small" title="What the pages say">
                    <Paragraph>{synthesis.pages_note}</Paragraph>
                </Card>
            )}
            <Card size="small" title="What the competitors are credited with">
                <Paragraph>{synthesis.competitor_note}</Paragraph>
            </Card>
            <Card size="small" title={`Record fixes (${synthesis.record_fixes.length})`}>
                <Table
                    size="small" pagination={false} rowKey="title"
                    dataSource={synthesis.record_fixes}
                    columns={[
                        { title: 'Element', dataIndex: 'element' },
                        { title: 'Fix', dataIndex: 'title' },
                        { title: 'Level', dataIndex: 'level', render: (l: string) => <Tag>{l}</Tag> },
                        { title: 'Cites', dataIndex: 'citations', render: (c: string[]) => c.join(', ') },
                    ]}
                />
            </Card>
        </Space>
    );


    // v4: what was fetched and read, which pages go with a higher or lower
    // rank, and what the reviewer left in the written read. Checked here
    // before the brand sees the read.
    const pageRows: ModelBeliefsPageRow[] = pages?.pages ?? [];
    const issues = detail?.synthesisIssues ?? null;
    const issueCount = (issues?.checks.length ?? 0) + (issues?.review.length ?? 0);
    const countBy = (key: (p: ModelBeliefsPageRow) => string) =>
        pageRows.reduce<Record<string, number>>((acc, p) => {
            const k = key(p);
            acc[k] = (acc[k] ?? 0) + 1;
            return acc;
        }, {});
    const byStatus = countBy((p) => p.readStatus);
    const byFetch = countBy((p) => p.fetchVia ?? 'not fetched');
    const fetched = pageRows.filter((p) => p.fetchVia !== null && p.fetchVia !== 'none').length;
    const unfetchable = byFetch.none ?? 0;
    const readProgress = pages?.reading
        ? `Reading pages: ${fetched} of ${pageRows.length} fetched, ${byStatus.ok ?? 0} read` +
          (unfetchable > 0 ? `, ${unfetchable} could not be fetched` : '')
        : null;
    const tokens = pageRows.reduce((sum, p) => sum + (p.inputTokens ?? 0) + (p.outputTokens ?? 0), 0);
    const effectModels = (sources?.pageEffects?.models ?? []).map((m) => {
        const brand = m.outcomes.find((o) => o.isBrand);
        const clear = (brand?.effects ?? []).filter((e) => e.clear && e.featureType === 'domain');
        const label = detail?.panel?.find((p) => p.provider === m.provider && p.model === m.model)?.label ?? m.model;
        return { key: `${m.provider}:${m.model}`, model: label, answers: m.answers, clear };
    });

    const pagesTab = (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space wrap>
                <Popconfirm
                    title="Read the pages again?"
                    description={
                        <Space direction="vertical" size={6}>
                            <Text>
                                Recollects the pages, fetches what is new, reads what is unread, re-aggregates what the pages say and
                                re-writes the read. Pages already read are kept. Spends about six reader calls a page unread, plus
                                Firecrawl credits.
                            </Text>
                            <Checkbox checked={retryUnreadable} onChange={(e) => setRetryUnreadable(e.target.checked)}>
                                Try again the pages we could not fetch last time
                            </Checkbox>
                        </Space>
                    }
                    okText="Read pages"
                    onConfirm={() => void readPagesAgain()}
                >
                    <Button
                        size="small"
                        icon={<ReadOutlined style={{ marginRight: 4 }} />}
                        loading={startingRead}
                        disabled={pages?.reading === true}
                    >
                        Read pages again
                    </Button>
                </Popconfirm>
                {readProgress && (
                    <Text style={{ color: '#fa8c16' }}>
                        <LoadingOutlined style={{ marginRight: 6 }} />
                        {readProgress}
                    </Text>
                )}
            </Space>
            {issues && (
                issueCount === 0 ? (
                    <Alert
                        type="success" showIcon
                        message={issues.reviewed
                            ? `Written read: no known problems${issues.retried ? ' after one corrective retry' : ''}.`
                            : 'Written read: code checks passed. The reviewer was off for this read.'}
                    />
                ) : (
                    <Alert
                        type="warning" showIcon icon={<WarningOutlined />}
                        message={`Written read: ${issueCount} problem(s) still in it after the corrective retry`}
                        description={
                            <Space direction="vertical" size={6}>
                                {issues.checks.map((c, i) => (
                                    <Text key={`c${i}`}><Tag>{c.kind}</Tag>{c.match}</Text>
                                ))}
                                {issues.review.map((p, i) => (
                                    <Space key={`r${i}`} direction="vertical" size={0}>
                                        <Text><Tag color="orange">{p.kind}</Tag>{p.statement && <Tag>{p.statement}</Tag>}<Text italic>“{p.sentence}”</Text></Text>
                                        {p.inputSays && <Text type="secondary" style={{ fontSize: 12 }}>Input says: {p.inputSays}</Text>}
                                    </Space>
                                ))}
                            </Space>
                        }
                    />
                )
            )}

            {pageRows.length === 0 ? (
                <Empty description={pages?.reading ? 'Collecting the pages now' : 'No pages read on this run'} />
            ) : (
                <>
                    <Row gutter={16}>
                        <Col span={6}><Card><Statistic title="Pages collected" value={pageRows.length} /></Card></Col>
                        <Col span={6}><Card><Statistic title="Read" value={byStatus.ok ?? 0} suffix={`/ ${pageRows.length}`} /></Card></Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Failed / skipped"
                                    value={`${byStatus.failed ?? 0} / ${byStatus.skipped ?? 0}`}
                                />
                            </Card>
                        </Col>
                        <Col span={6}><Card><Statistic title="Reader tokens" value={tokens.toLocaleString()} /></Card></Col>
                    </Row>
                    <Text type="secondary">
                        Fetched: {Object.entries(byFetch).map(([k, v]) => `${k} ${v}`).join(' · ')}
                        {pages?.settings && ` · read when at least ${pages.settings.minAnswers ?? '-'} answers read the page, ${pages.settings.maxChars ?? '-'} characters`}
                    </Text>
                </>
            )}

            {effectModels.length > 0 && (
                <Card
                    size="small"
                    title={
                        <Space size={4}>
                            Pages that move the brand's rank
                            <Tooltip title="Per model: the brand's rank score in the answers that read a page against the answers that did not. Only pages read in at least 10 answers and missed by at least 10, and only differences whose 90% interval excludes zero. Association, not cause.">
                                <InfoCircleOutlined />
                            </Tooltip>
                        </Space>
                    }
                >
                    <Table
                        size="small" pagination={false}
                        rowKey={(r) => `${r.model}|${r.feature}`}
                        dataSource={effectModels.flatMap((m) => m.clear.map((e) => ({ ...e, model: m.model, answers: m.answers })))}
                        locale={{ emptyText: 'No page clearly moves the brand\'s rank on any model' }}
                        columns={[
                            { title: 'Model', dataIndex: 'model' },
                            { title: 'Page', dataIndex: 'feature' },
                            { title: 'Kind', dataIndex: 'kind', render: (k: string) => <Tag>{k}</Tag> },
                            {
                                title: 'Rank score read / not',
                                render: (_: unknown, e: any) => (
                                    <Text style={{ color: e.diff > 0 ? '#237804' : '#cf1322' }}>
                                        {e.scorePresent.toFixed(1)} / {e.scoreAbsent.toFixed(1)}
                                    </Text>
                                ),
                            },
                            { title: 'Listed read / not', render: (_: unknown, e: any) => `${pct(e.listedPresent)} / ${pct(e.listedAbsent)}` },
                            { title: 'Answers read / not', render: (_: unknown, e: any) => `${e.nPresent} / ${e.nAbsent}` },
                        ]}
                    />
                </Card>
            )}

            {pageRows.length > 0 && (
                <Card size="small" title="Every page">
                    <Table
                        size="small" rowKey="id"
                        pagination={{ pageSize: 25, hideOnSinglePage: true }}
                        dataSource={pageRows}
                        columns={[
                            {
                                title: 'Page',
                                dataIndex: 'domain',
                                render: (d: string, p: ModelBeliefsPageRow) => (
                                    <Space direction="vertical" size={0}>
                                        <a href={p.url} target="_blank" rel="noopener noreferrer">{d}</a>
                                        {p.title && <Text type="secondary" style={{ fontSize: 12 }}>{p.title}</Text>}
                                    </Space>
                                ),
                            },
                            { title: 'Answers', dataIndex: 'answersTotal', width: 80 },
                            {
                                title: 'Kind', dataIndex: 'kind', width: 120,
                                render: (k: string, p: ModelBeliefsPageRow) => (
                                    <Space size={2} wrap><Tag>{k}</Tag>{p.listicle && <Tag color="blue">list</Tag>}</Space>
                                ),
                            },
                            {
                                title: 'Fetched', dataIndex: 'fetchVia', width: 110,
                                render: (v: string | null, p: ModelBeliefsPageRow) => (
                                    <Tooltip title={`${p.chars.toLocaleString()} characters${p.fetchStatus ? `, HTTP ${p.fetchStatus}` : ''}`}>
                                        <Tag color={v ? undefined : 'default'}>{v ?? 'no'}</Tag>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Read', dataIndex: 'readStatus', width: 100,
                                render: (v: string, p: ModelBeliefsPageRow) => (
                                    <Tooltip title={p.error ?? undefined}>
                                        <Tag color={v === 'ok' ? 'green' : v === 'failed' ? 'red' : 'default'}>{v}</Tag>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Statements covered', width: 150,
                                render: (_: unknown, p: ModelBeliefsPageRow) => {
                                    const scores = p.scoresJson ?? [];
                                    const covered = scores.filter((x) => x.covered && x.score !== null).length;
                                    return scores.length === 0 ? '-' : `${covered} of ${scores.length}`;
                                },
                            },
                            {
                                title: 'Tokens', width: 110,
                                render: (_: unknown, p: ModelBeliefsPageRow) => (p.inputTokens + p.outputTokens).toLocaleString(),
                            },
                        ]}
                        expandable={{
                            rowExpandable: (p: ModelBeliefsPageRow) => (p.scoresJson ?? []).some((x) => x.covered),
                            expandedRowRender: (p: ModelBeliefsPageRow) => (
                                <Space direction="vertical" size={4}>
                                    {(p.scoresJson ?? []).filter((x) => x.covered).map((x) => (
                                        <Text key={x.code} style={{ fontSize: 12.5 }}>
                                            <Tag>{x.code}</Tag><Text strong>{x.score}</Text>{' '}
                                            <Text type={x.quoteFound === false ? 'danger' : undefined}>“{x.quote}”</Text>
                                            {x.quoteFound === false && <Text type="danger"> (not found on the page)</Text>}
                                        </Text>
                                    ))}
                                </Space>
                            ),
                        }}
                    />
                </Card>
            )}
        </Space>
    );

    return (
        <Tabs
            items={[
                { key: 'gate', label: 'Awareness gate', children: gateTab },
                { key: 'beliefs', label: 'Beliefs', children: beliefsTab },
                { key: 'sources', label: 'Sources', children: sourcesTab },
                {
                    key: 'pages',
                    label: (
                        <span>
                            Pages
                            {issueCount > 0 && <Tag color="orange" style={{ marginLeft: 6 }}>{issueCount}</Tag>}
                        </span>
                    ),
                    children: pagesTab,
                },
                { key: 'synthesis', label: 'Synthesis', children: synthesisTab },
            ]}
        />
    );
};
